import { observeProximity } from "../shared/cables-runtime";
import {
  isProductAnimationSource,
  PRODUCT_ANIMATION_SOURCE,
  setProductAnimationState,
} from "./animation-state";

export function initProductSlider({
  enableFramePlayerEverywhere = false,
  replaceCablesScene = false,
} = {}) {
  const DESKTOP_QUERY = "(min-width: 1025px)";

  const desktopMedia = window.matchMedia(DESKTOP_QUERY);
  const initialScrollBehavior = document.documentElement.style.scrollBehavior;

  const wrap = document.querySelector(".a-animation--wrap-main");
  if (!wrap) return;

  const items = [];
  const links = Array.from(wrap.querySelectorAll(".a-animation--link"));

  let progress = 0;
  let rafId = null;
  let videoRafId = null;
  let scrollTriggerInstance = null;
  let hasAppliedDesktopReset = false;
  let activeFramePlayer = null;
  let activeFrameMountPromise = null;
  let activeFrameReady = false;
  let framePlayerEnabled = false;
  let isNearAnimation = false;
  let isDestroyed = false;
  let lastActiveIndex = 0;
  let linkSyncTicking = false;
  let linkClassObserver = null;

  const isMobileMode = () => !desktopMedia.matches;

  function applyDesktopScrollReset() {
    if (!desktopMedia.matches || hasAppliedDesktopReset || !window.ScrollTrigger) return;

    hasAppliedDesktopReset = true;
    document.documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);

    if (typeof ScrollTrigger.clearScrollMemory === "function") {
      ScrollTrigger.clearScrollMemory();
    }
  }

  function restoreScrollBehavior() {
    document.documentElement.style.scrollBehavior = initialScrollBehavior;
  }

  function renderPagination() {
    rafId = null;

    items.forEach((item) => {
      item.active.style.transform = `translate3d(${(item.x * progress).toFixed(2)}px, ${(item.y * progress).toFixed(2)}px, 0)`;
    });
  }

  function requestPaginationRender() {
    if (rafId) return;
    rafId = requestAnimationFrame(renderPagination);
  }

  function measurePagination() {
    items.length = 0;

    wrap.querySelectorAll(".slider__pagination").forEach((pagination) => {
      const active = pagination.querySelector(".track__active");
      const lines = Array.from(pagination.querySelectorAll(".track__line"));
      if (!active || !lines.length) return;

      active.style.transform = "translate3d(0, 0, 0)";

      const activeRect = active.getBoundingClientRect();
      const lastLineRect = lines[lines.length - 1].getBoundingClientRect();

      const activeCenterX = activeRect.left + activeRect.width / 2;
      const activeCenterY = activeRect.top + activeRect.height / 2;
      const lastCenterX = lastLineRect.left + lastLineRect.width / 2;
      const lastCenterY = lastLineRect.top + lastLineRect.height / 2;

      if (isMobileMode()) {
        items.push({
          active,
          x: lastCenterX - activeCenterX,
          y: 0,
        });
      } else {
        items.push({
          active,
          x: 0,
          y: lastCenterY - activeCenterY,
        });
      }
    });

    renderPagination();
  }

  function createPaginationScrollTrigger() {
    if (scrollTriggerInstance || !window.ScrollTrigger) return;

    scrollTriggerInstance = ScrollTrigger.create({
      trigger: wrap,
      start: "top bottom",
      end: "bottom top",
      onUpdate: (self) => {
        progress = self.progress;
        requestPaginationRender();
      },
      onRefresh: measurePagination,
    });
  }

  function getActiveFramePlayer() {
    if (activeFrameReady && activeFramePlayer) {
      return Promise.resolve(activeFramePlayer);
    }

    if (activeFrameMountPromise) {
      return activeFrameMountPromise;
    }

    const canvas = document.getElementById("active-frame-canvas");

    if (!canvas) {
      return Promise.resolve(null);
    }

    if (!window.ActiveFramePlayer) {
      setProductAnimationState(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME, "error");
      console.error(
        "[ActiveFrame] Player is unavailable. Load activeframe-player.bundle.min.js before the homepage bundle."
      );
      return Promise.resolve(null);
    }

    let sourceFailed = false;
    let sourceLoaded = false;

    activeFramePlayer = window.ActiveFramePlayer.createActiveFramePlayer({
      canvas,
      src: "https://d2my2wpsc41l6t.cloudfront.net/modernexpo/main/modex_mobile_v3.af",
      fit: "cover",
      hardwareAcceleration: "prefer-hardware",
      autoResize: true,
      onReady: (instance) => {
        sourceLoaded = true;
        canvas.dataset.activeFramePlayerReady = "true";
        canvas.dataset.activeFrameTotalFrames = String(instance.getState().totalFrames ?? "");
      },
      onError: (error) => {
        sourceFailed = true;
        if (isProductAnimationSource(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME)) {
          setProductAnimationState(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME, "error");
        }
        console.error("[ActiveFrame] Failed to load product animation", error);
      },
    });

    activeFrameMountPromise = activeFramePlayer
      .mount()
      .then(() => {
        if (sourceFailed) {
          throw new Error("ActiveFrame source reported a loading error");
        }

        if (!sourceLoaded && activeFramePlayer.getState?.().loaded !== true) {
          throw new Error("ActiveFrame source did not finish loading");
        }

        activeFrameReady = true;
        activeFramePlayer.setFrame(0, { force: true });
        return activeFramePlayer;
      })
      .catch((error) => {
        if (isProductAnimationSource(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME)) {
          setProductAnimationState(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME, "error");
        }
        console.error("[ActiveFrame] Failed to mount product animation", error);
        activeFramePlayer = null;
        activeFrameMountPromise = null;
        activeFrameReady = false;
        return null;
      });

    return activeFrameMountPromise;
  }

  function getVideoProgressFromTrack() {
    const rect = wrap.getBoundingClientRect();
    const scrollDistance = rect.height - window.innerHeight;

    if (scrollDistance <= 0) return 0;

    const rawProgress = -rect.top / scrollDistance;

    return Math.min(Math.max(rawProgress, 0), 1);
  }

  function renderVideoProgress() {
    videoRafId = null;

    if (!framePlayerEnabled || !activeFrameReady || !activeFramePlayer) return;

    const currentVideoProgress = getVideoProgressFromTrack();

    activeFramePlayer.setProgress(currentVideoProgress, { force: false });
  }

  function requestVideoUpdate() {
    if (!framePlayerEnabled || !activeFrameReady || !activeFramePlayer) return;
    if (videoRafId) return;

    videoRafId = requestAnimationFrame(renderVideoProgress);
  }

  async function enableFramePlayer() {
    if (framePlayerEnabled || !isNearAnimation || isDestroyed) return;

    framePlayerEnabled = true;
    setProductAnimationState(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME, "loading");

    const player = await getActiveFramePlayer();
    if (!framePlayerEnabled || !player) return;

    setProductAnimationState(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME, "ready");
    requestVideoUpdate();
  }

  function disableFramePlayer() {
    framePlayerEnabled = false;

    if (isProductAnimationSource(wrap, PRODUCT_ANIMATION_SOURCE.ACTIVE_FRAME)) {
      setProductAnimationState(wrap, PRODUCT_ANIMATION_SOURCE.CABLES, "loading");
    }

    if (videoRafId) {
      cancelAnimationFrame(videoRafId);
      videoRafId = null;
    }
  }

  function clearFallbackCurrent() {
    links.forEach((link) => {
      if (link.dataset.mobileFallbackCurrent === "true") {
        link.classList.remove("w--current");
        delete link.dataset.mobileFallbackCurrent;
      }
    });
  }

  function setFallbackCurrent(index) {
    links.forEach((link, i) => {
      if (i === index) {
        link.classList.add("w--current");
        link.dataset.mobileFallbackCurrent = "true";
      } else if (link.dataset.mobileFallbackCurrent === "true") {
        link.classList.remove("w--current");
        delete link.dataset.mobileFallbackCurrent;
      }
    });
  }

  function syncMobileCurrentLink() {
    if (!isMobileMode()) {
      clearFallbackCurrent();
      return;
    }

    const currentLinks = links.filter((link) => link.classList.contains("w--current"));
    const realCurrentLinks = currentLinks.filter(
      (link) => link.dataset.mobileFallbackCurrent !== "true"
    );

    if (realCurrentLinks.length) {
      clearFallbackCurrent();
      lastActiveIndex = links.indexOf(realCurrentLinks[realCurrentLinks.length - 1]);
      return;
    }

    if (currentLinks.length) {
      lastActiveIndex = links.indexOf(currentLinks[currentLinks.length - 1]);
      return;
    }

    setFallbackCurrent(lastActiveIndex >= 0 ? lastActiveIndex : 0);
  }

  function requestMobileCurrentLinkSync() {
    if (linkSyncTicking) return;

    linkSyncTicking = true;

    requestAnimationFrame(() => {
      linkSyncTicking = false;
      syncMobileCurrentLink();
    });
  }

  function initMobileCurrentLinkFallback() {
    if (!links.length || typeof window.MutationObserver !== "function") return;

    linkClassObserver = new window.MutationObserver(requestMobileCurrentLinkSync);

    links.forEach((link) => {
      linkClassObserver.observe(link, { attributes: true, attributeFilter: ["class"] });
    });

    requestMobileCurrentLinkSync();
  }

  function handleScroll() {
    requestVideoUpdate();
    requestMobileCurrentLinkSync();
  }

  function handleResize() {
    measurePagination();

    if (window.ScrollTrigger) {
      ScrollTrigger.refresh();
    }

    requestVideoUpdate();
    requestMobileCurrentLinkSync();
  }

  function syncBreakpointMode() {
    if (desktopMedia.matches) {
      applyDesktopScrollReset();
      restoreScrollBehavior();

      if (enableFramePlayerEverywhere) {
        enableFramePlayer();
      } else {
        disableFramePlayer();
      }
    } else {
      restoreScrollBehavior();
      enableFramePlayer();
    }

    measurePagination();

    if (window.ScrollTrigger) {
      ScrollTrigger.refresh();
    }

    requestMobileCurrentLinkSync();
  }

  if (replaceCablesScene) {
    const cablesCanvas = document.querySelector(
      ".a-animation--wrap-secondary .scene-scroller:not(#active-frame-canvas)"
    );
    const frameCanvas = document.getElementById("active-frame-canvas");

    if (cablesCanvas && frameCanvas) {
      cablesCanvas.style.display = "none";
      cablesCanvas.dataset.activeFrameReplacement = "true";
    }

    if (frameCanvas) {
      frameCanvas.style.display = "block";
      frameCanvas.dataset.activeFrameReplacement = "true";
    }
  }

  createPaginationScrollTrigger();
  initMobileCurrentLinkFallback();

  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize);

  const proximityObserver = observeProximity(
    wrap,
    () => {
      isNearAnimation = true;
      if (!desktopMedia.matches || enableFramePlayerEverywhere) enableFramePlayer();
    },
    () => {
      isNearAnimation = false;
    },
    `${window.innerHeight}px 0px`
  );

  syncBreakpointMode();

  if (desktopMedia.addEventListener) {
    desktopMedia.addEventListener("change", syncBreakpointMode);
  } else if (desktopMedia.addListener) {
    desktopMedia.addListener(syncBreakpointMode);
  }

  return {
    get framePlayerMountPromise() {
      return activeFrameMountPromise;
    },
    destroy() {
      isDestroyed = true;
      proximityObserver?.disconnect();
      disableFramePlayer();
      activeFramePlayer?.destroy?.();
      linkClassObserver?.disconnect();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);

      if (desktopMedia.removeEventListener) {
        desktopMedia.removeEventListener("change", syncBreakpointMode);
      } else if (desktopMedia.removeListener) {
        desktopMedia.removeListener(syncBreakpointMode);
      }
    },
  };
}

export function bootProductSlider(options) {
  const start = () => initProductSlider(options);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}
